import { useState } from 'react';
import clsx from 'clsx';
import { Modal, Typography } from '@ag1/ui';
import { type TestimonialsV5 } from 'types/studio/studio';
import { Direction, useOnSwipe } from 'shared/hooks';
import { findIndexWrapAround } from 'shared/utils/findIndexWrapAround';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import TestimonialsFlyoutArrows from '../TestimonialsFlyoutArrows/TestimonialsFlyoutArrows';
import styles from './styles.module.scss';

type TestimonialsFlyoutProps = {
  opened: boolean;
  selected: any;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  cards: TestimonialsV5['cards'];
  modalSuperHeadline: TestimonialsV5['modalSuperHeadline'];
  footnote?: TestimonialsV5['footnote'];
  closeFlyout: () => void;
};

const TestimonialsFlyout = ({
  opened,
  selected,
  setSelected,
  cards,
  modalSuperHeadline,
  footnote,
  closeFlyout
}: TestimonialsFlyoutProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const i = cards ? cards.findIndex((card) => card === selected) : 0;

  const handleArrowClick = (offset: number) => {
    if (cards) {
      const newIndex = findIndexWrapAround(i, cards.length, offset);
      setImageLoaded(false);
      setSelected(cards[newIndex]);
    }
  };

  const touchEvents = useOnSwipe(Direction.horizontal, {
    onSwipeLeft: () => {
      handleArrowClick(+1);
    },
    onSwipeRight: () => {
      handleArrowClick(-1);
    }
  });

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const { image, title, subtitle, text } = selected;

  return (
    <Modal animation="slide" isOpen={opened} onClose={closeFlyout} noDivider>
      <Modal.Header className={styles.modalHeader}>
        {modalSuperHeadline && (
          <Typography variant="bodyMono" size="1" noPadding className={styles.modalSuperHeadline}>
            {modalSuperHeadline}
          </Typography>
        )}
      </Modal.Header>
      <Modal.Content className={styles.modalContent}>
        <div className={styles.imageWrapper} {...touchEvents}>
          {!imageLoaded && <div className={styles.skeleton} />}
          <StudioBaseImageV2
            image={image}
            className={clsx(styles.image, { [styles.imageLoaded]: imageLoaded })}
            onLoad={handleImageLoad}
          />
        </div>
        {title && (
          <Typography
            component="h2"
            variant="headlineSans"
            size="3"
            weight="400"
            noPadding
            className={styles.title}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body" size="1" noPadding className={styles.subtitle}>
            {subtitle}
          </Typography>
        )}
        {text && (
          <Typography
            component="p"
            variant="body"
            size="1"
            weight="400"
            noPadding
            color="black-new"
            className={styles.text}>
            <CustomPortableText value={text} />
          </Typography>
        )}
        {footnote && (
          <Typography
            variant="portalCaption"
            size="2"
            color="grey-1-new"
            noPadding
            className={styles.footnote}>
            <CustomPortableText value={footnote} />
          </Typography>
        )}
      </Modal.Content>
      <Modal.Footer className={styles.modalFooter}>
        <TestimonialsFlyoutArrows
          onClickPrev={() => handleArrowClick(-1)}
          onClickNext={() => handleArrowClick(1)}
        />
        <Typography variant="body" size="1" noPadding className={styles.cardsLengthText}>
          <span>{i + 1}</span> of {cards && cards.length}
        </Typography>
      </Modal.Footer>
    </Modal>
  );
};

export default TestimonialsFlyout;
